import type {
  GroupFile,
  GroupMember,
} from '@app/application/user-management/types'

import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import {
  create,
  destroy,
  get,
  update,
} from '@app/application/user-management/api/groups'
import {
  GROUP_PANEL_REQUEST_KEY,
  STALE_GROUP_PANEL_REQUEST_REASON,
  UNPROCESSABLE_ENTITY,
} from '@app/application/user-management/constants'
import { abort } from '@app/adapters/abort'
import { isValidationErrorBody } from '@app/application/user-management/predicates/is-validation-error-body'
import { startLatestRequest } from '@app/application/user-management/utils/list-query'
import { isResponseError } from '@app/utils/predicates'
import useI18n from '@app/shared/vue/composables/use-I18n'

const validationErrors = (error: unknown): Record<string, string[]> | null =>
  isResponseError(error) &&
  error.status === UNPROCESSABLE_ENTITY &&
  isValidationErrorBody(error.body)
    ? error.body.errors
    : null

export const useGroupPanelStore = defineStore('groupPanel', () => {
  const I18n = useI18n()

  const isOpen = ref(false)
  const openGroupId = ref<number | null>(null)
  const name = ref('')
  const members = ref<GroupMember[]>([])
  const files = ref<GroupFile[]>([])
  const loading = ref(false)
  const loadError = ref(false)
  const saving = ref(false)
  const deleting = ref(false)
  const confirmingDelete = ref(false)
  const saveError = ref<string | null>(null)
  const nameErrors = ref<string[]>([])

  const creating = computed(() => isOpen.value && openGroupId.value === null)

  function reset(): void {
    name.value = ''
    members.value = []
    files.value = []
    nameErrors.value = []
    saveError.value = null
    loading.value = false
    loadError.value = false
    confirmingDelete.value = false
  }

  // A 422 answers `{errors: {<attribute>: [<full message>]}}`. The panel shows
  // name errors under the field and joins all other messages into `saveError`.
  function reportSaveFailure(error: unknown): void {
    const errors = validationErrors(error)

    if (!errors) {
      saveError.value = I18n.t('group.panel.save_error')

      return
    }

    nameErrors.value = errors.name ?? []

    const others = Object.entries(errors)
      .filter(([attribute]) => attribute !== 'name')
      .flatMap(([, messages]) => messages)

    if (others.length) saveError.value = others.join(' ')
    if (!nameErrors.value.length && !others.length) {
      saveError.value = I18n.t('group.panel.save_error')
    }
  }

  async function open(id: number): Promise<void> {
    const isStale = startLatestRequest(
      GROUP_PANEL_REQUEST_KEY,
      STALE_GROUP_PANEL_REQUEST_REASON
    )

    reset()
    isOpen.value = true
    openGroupId.value = id
    loading.value = true

    try {
      const details = await get(id)

      if (isStale()) return

      if (details) {
        name.value = details.name
        members.value = details.members
        files.value = details.files
      }
    } catch {
      if (!isStale()) loadError.value = true
    } finally {
      if (!isStale()) loading.value = false
    }
  }

  function openNew(): void {
    abort(GROUP_PANEL_REQUEST_KEY, STALE_GROUP_PANEL_REQUEST_REASON)
    reset()
    isOpen.value = true
    openGroupId.value = null
  }

  function close(): void {
    abort(GROUP_PANEL_REQUEST_KEY, STALE_GROUP_PANEL_REQUEST_REASON)
    reset()
    isOpen.value = false
    openGroupId.value = null
  }

  function addMember(member: GroupMember): void {
    members.value = [...members.value, member]
  }

  function removeMember(id: number): void {
    members.value = members.value.filter((member) => member.id !== id)
  }

  function addFile(file: GroupFile): void {
    files.value = [...files.value, file]
  }

  function removeFile(id: number): void {
    files.value = files.value.filter((file) => file.id !== id)
  }

  async function save(): Promise<boolean> {
    const id = openGroupId.value

    nameErrors.value = []
    saveError.value = null
    saving.value = true

    const group = {
      name: name.value,
      userIds: members.value.map((member) => member.id),
      companyIds: files.value.map((file) => file.id),
    }

    try {
      if (id === null) {
        await create(group)
      } else {
        await update(id, group)
      }

      return true
    } catch (error) {
      reportSaveFailure(error)

      return false
    } finally {
      saving.value = false
    }
  }

  async function remove(): Promise<boolean> {
    const id = openGroupId.value

    if (id === null) return false

    deleting.value = true

    try {
      await destroy(id)

      return true
    } catch {
      saveError.value = I18n.t('group.panel.delete_error')

      return false
    } finally {
      confirmingDelete.value = false
      deleting.value = false
    }
  }

  return {
    addFile,
    addMember,
    close,
    confirmingDelete,
    creating,
    deleting,
    files,
    isOpen,
    loadError,
    loading,
    members,
    name,
    nameErrors,
    open,
    openGroupId,
    openNew,
    remove,
    removeFile,
    removeMember,
    save,
    saveError,
    saving,
  }
})
